import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const PricingSection = () => {
  const plans = [
    {
      name: "Neil AI Essential™",
      price: "$197",
      period: "mo",
      subtitle: "Your first AI employee — for startups, churches, contractors & local businesses",
      features: [
        "AI CRM System — pipelines, contacts, lead tracking, mobile app",
        "AI Lead Response — website, Facebook, missed calls, text inquiries",
        "Missed Call Text Back",
        "AI Website Chat Assistant (24/7, trained on your business)",
        "Email + SMS Automation — follow-ups, reminders, nurture",
        "Online Booking System",
        "Review Automation",
        "5 Core Automations Included",
      ],
      tagline: "Your first AI employee.",
      ctaText: "Get Started",
      ctaLink: "/book",
    },
    {
      name: "Neil AI Growth™",
      price: "$497",
      period: "mo",
      subtitle: "AI receptionist + sales assistant working 24/7 — for medical, restaurants, gyms, retail",
      badge: "Most Popular",
      features: [
        "Everything in Essential™, plus:",
        "AI Voice Agent — answers calls, qualifies leads, books appointments, handles FAQs",
        "AI Customer Service Employee",
        "AI Appointment Setter",
        "Advanced CRM Automation & Smart Sales Pipelines",
        "Facebook + Google Lead Integration",
        "Social Media Scheduler",
        "Lead Reactivation Campaigns",
        "Advanced Dashboards",
        "Up to 15 Custom Workflows",
      ],
      tagline: "An AI receptionist, follow-up manager, and sales assistant working 24/7.",
      ctaText: "Upgrade Now",
      ctaLink: "/book",
      featured: true,
    },
    {
      name: "Neil AI Dominate™",
      price: "$897",
      period: "mo",
      subtitle: "A full AI workforce — for scaling, multi-location, hospitality, healthcare",
      features: [
        "Everything in Growth™, plus:",
        "Multi-AI Employee Ecosystem (receptionist, sales, support, follow-up, booking, reviews)",
        "Advanced AI Voice System — inbound + outbound, nurturing, confirmations, recovery",
        "AI Sales Automation System",
        "Advanced Workflow Engine + Internal Team Notifications",
        "AI Lead Scoring",
        "Advanced KPI Reporting",
        "Custom Funnels + Landing Pages",
        "Review + Reputation Management Suite",
        "Unlimited Pipelines",
        "Monthly Strategy + Optimization Call",
      ],
      tagline: "Your business now operates with a full AI workforce.",
      ctaText: "Book Strategy Call",
      ctaLink: "/book",
      gradient: true,
    },
    {
      name: "Neil AI Titan™",
      price: "$1,200+",
      period: "mo",
      subtitle: "Full AI Business Operating System™ — enterprise, hospitals, franchises, logistics",
      badge: "Enterprise",
      features: [
        "Everything in Dominate™, plus:",
        "Multiple AI Voice Agents (sales, support, recruiter, scheduling, outbound, qualifier)",
        "Advanced AI Employee Network (ops, marketing, onboarding, client success)",
        "Enterprise CRM Infrastructure",
        "AI Call Center System",
        "Multi-Location Management",
        "Enterprise Automation Architecture",
        "Advanced Analytics + Executive KPI Dashboards",
        "Custom API Integrations",
        "White-Glove Implementation + Priority Support",
        "Monthly Executive Consulting + Custom AI Growth Roadmap",
      ],
      tagline: "We transform companies into AI-powered enterprises.",
      ctaText: "Talk to Sales",
      ctaLink: "/book",
      elite: true,
    },
  ];

  return (
    <section id="pricing" className="py-16 space-y-8">
      <div className="text-center space-y-4">
        <Badge className="bg-primary/15 text-primary border border-primary/30 text-xs font-bold tracking-widest">
          AI BUSINESS OPERATING SYSTEM™ 4.0
        </Badge>
        <h2 className="text-3xl md:text-5xl font-bold font-orbitron text-foreground">
          We Install <span className="text-primary">AI Employees</span> Into Businesses
        </h2>
        <p className="text-muted-foreground text-base max-w-3xl mx-auto">
          Automate. Elevate. Dominate. Choose the AI workforce package that matches where your business is today.
        </p>
      </div>

      {/* Exclusive Offer Banner */}
      <div className="max-w-5xl mx-auto p-6 md:p-8 rounded-2xl border-2 border-primary/40 bg-gradient-to-r from-primary/10 via-card to-primary/10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          background: "radial-gradient(circle at 30% 50%, hsl(var(--primary) / 0.3), transparent 60%)"
        }} />
        <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          <div className="text-4xl md:text-5xl">🤖</div>
          <div className="flex-1">
            <Badge className="mb-2 bg-primary text-primary-foreground text-xs font-bold">EXCLUSIVE OFFER</Badge>
            <h3 className="text-xl md:text-2xl font-bold font-orbitron text-foreground mb-2">
              Buy 1 Robot → Get 1 AI Employee FREE for 6 Months™
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              Purchase a PUDU CC1 Pro, MT1, BellaBot Pro, KettyBot Pro, or PuduBot 2 — and choose one free AI employee:
              Receptionist · Appointment Setter · Lead Follow-Up · Customer Support · Review Agent · Booking Assistant · FAQ Assistant.
            </p>
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
              <a href="/book">Claim This Offer</a>
            </Button>
          </div>
        </div>
      </div>

      {/* Setup Fee Notice */}
      <div className="max-w-4xl mx-auto p-5 border border-primary/30 rounded-xl bg-card/60 text-center">
        <p className="text-sm text-foreground">
          <span className="font-bold text-primary">Required Setup & Implementation Fee: $500–$1,500+</span>
          <span className="text-muted-foreground"> — includes CRM configuration, workflow creation, AI training, integrations, pipelines, calendars, voice AI setup, onboarding & custom branding.</span>
        </p>
      </div>

      {/* 4-Column Pricing Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5 items-stretch">
        {plans.map((plan, index) => (
          <Card
            key={index}
            className={`relative flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
              plan.featured
                ? 'border-2 border-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]'
                : plan.elite
                ? 'border-2 border-cyan-400 shadow-[0_0_0_4px_rgba(127,255,212,0.15)] bg-gradient-to-b from-card to-card/90'
                : plan.gradient
                ? 'bg-gradient-to-b from-card to-card/80 border-primary/20'
                : 'border-border'
            }`}
          >
            {plan.badge && (
              <Badge className="absolute -top-3 left-3 bg-primary text-primary-foreground px-3 py-1 text-xs font-bold border-0">
                {plan.badge}
              </Badge>
            )}

            <CardHeader className="space-y-3 pb-4 flex-grow">
              <div>
                <CardTitle className="text-xl mb-2 font-orbitron">{plan.name}</CardTitle>
                {plan.subtitle && (
                  <p className="text-sm text-muted-foreground mb-3">{plan.subtitle}</p>
                )}
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="text-3xl font-bold text-primary">{plan.price}</span>
                  <span className="text-muted-foreground text-sm">/{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-2.5 text-sm text-muted-foreground leading-relaxed">
                {plan.features.map((feature, i) => (
                  <li key={i} className={feature.startsWith("Everything in") ? "font-semibold text-foreground" : ""}>
                    {feature.startsWith("Everything in") ? feature : `✓ ${feature}`}
                  </li>
                ))}
              </ul>

              {plan.tagline && (
                <p className="text-xs text-foreground/80 italic pt-2 font-medium border-t border-border/50 mt-3">
                  "{plan.tagline}"
                </p>
              )}
            </CardHeader>

            <CardContent className="px-6 pb-6 pt-0">
              <Button
                asChild
                className={`w-full font-bold ${
                  plan.elite
                    ? 'bg-cyan-400 hover:bg-cyan-500 text-card'
                    : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                }`}
              >
                <a href={plan.ctaLink}>{plan.ctaText}</a>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Optional Add-Ons */}
      <div className="mt-12 p-6 border border-border rounded-xl bg-card/50">
        <h3 className="text-xl font-bold mb-4 text-foreground font-orbitron">Optional Add-Ons (Any Plan)</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">AI Content Creator System</h4>
            <p className="text-xs text-muted-foreground mb-2">Viral strategy, captions, short-form scripts, reels, content calendars, social automation.</p>
            <p className="text-lg font-bold text-primary mb-2">$297/mo</p>
            <Button asChild size="sm" className="w-full"><a href="/book">Add to Plan</a></Button>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">Advanced AI Voice Agents</h4>
            <p className="text-xs text-muted-foreground mb-2">Scaled by minutes, call volume, outbound campaigns & complexity.</p>
            <p className="text-lg font-bold text-primary mb-2">$197–$997/mo</p>
            <Button asChild size="sm" className="w-full"><a href="/book">Get Quote</a></Button>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">Website + Funnel Systems</h4>
            <p className="text-lg font-bold text-primary mb-2">$500–$5,000+</p>
            <Button asChild size="sm" className="w-full"><a href="/book">Request Build</a></Button>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">Paid Ads Management</h4>
            <p className="text-lg font-bold text-primary mb-2">From $500/mo</p>
            <Button asChild size="sm" className="w-full"><a href="/book">Start Campaign</a></Button>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">Custom AI Automation Builds</h4>
            <p className="text-lg font-bold text-primary mb-2">$500–$10,000+</p>
            <Button asChild size="sm" className="w-full"><a href="/book">Scope Project</a></Button>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">AI Clone Creator</h4>
            <p className="text-xs text-muted-foreground mb-2">Photorealistic digital twin + unlimited talking-avatar videos.</p>
            <Button asChild size="sm" className="w-full"><a href="/clone-creator">Explore Clone Creator</a></Button>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">Seedance 2.0 Video Generator</h4>
            <p className="text-xs text-muted-foreground mb-2">Done-for-you cinematic AI videos — up to 30/month.</p>
            <p className="text-lg font-bold text-primary mb-2">$135/mo</p>
            <Button asChild size="sm" className="w-full"><a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">Add</a></Button>
          </div>
        </div>
      </div>

      <div className="text-center space-y-3 pt-6 max-w-4xl mx-auto">
        <div className="p-4 border border-border/50 rounded-lg bg-card/80 text-left">
          <h4 className="text-sm font-bold text-foreground mb-2">📋 Pricing Terms & Transparency:</h4>
          <ul className="text-xs text-muted-foreground space-y-1.5">
            <li>✅ <strong>Setup Fee ($500–$1,500+):</strong> Charged at signup. Covers full implementation, training, and onboarding.</li>
            <li>✅ <strong>Monthly Subscription:</strong> Recurring charge for ongoing AI employees, support, and platform access.</li>
            <li>✅ <strong>No Hidden Fees:</strong> White-glove service and custom integrations are optional and quoted separately.</li>
            <li>✅ <strong>Cancel Anytime:</strong> Monthly plans can be cancelled. Setup fees are non-refundable after onboarding begins.</li>
            <li>✅ <strong>Data & Privacy:</strong> All data is encrypted and handled per our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
